import React, { useState, useMemo, useEffect } from "react";
import { TextField, Autocomplete, Box, Typography } from "@mui/material";

const SearchableCourseSelect = ({
  courses,
  value,
  onChange,
  loading = false,
  size = "small",
  fullWidth = true,
  label,
  required = false,
  sx,
  disablePortal = true,
  ...props
}) => {
  const [inputValue, setInputValue] = useState("");

  // Determine whether to show the loading indicator: only when loading AND no courses are present
  const showLoading = Boolean(loading && (!courses || courses.length === 0));

  // Helper to normalize course object from either external API shape or Firestore CcpCourses
  const getCourseDisplay = (course) => {
    if (!course) return { code: "", title: "" };

    // New Firestore shape: single Courses field
    if (course.Courses) {
      return { code: course.Courses, title: "" };
    }

    // Legacy shape
    return {
      code: course.Course_Nm || course.Course_Cd || "",
      title: course.Course_Title_Ds || "",
    };
  };

  // Determine selected option object from value (course code)
  const selectedOption = React.useMemo(() => {
    if (!value) return null;
    return courses.find((c) => getCourseDisplay(c).code === value) || null;
  }, [value, courses]);

  // Create a fallback option object matching the Firestore shape when
  // the courses list doesn't contain the selected code (e.g., delayed load).
  const fallbackOption = React.useMemo(() => {
    if (!value) return null;
    return { Courses: value };
  }, [value]);

  // Update input value when the selected value changes
  React.useEffect(() => {
    // Always derive the displayed input from the selected option (explicit object)
    // or fallbackOption. This is more robust when the courses list loads
    // asynchronously and prevents the input from showing the placeholder.
    const selected = selectedOption || fallbackOption;
    if (selected) {
      const { code, title } = getCourseDisplay(selected);
      const fullText = title ? `${code} - ${title}` : code;
      setInputValue(fullText);
    } else {
      setInputValue("");
    }
  }, [selectedOption, fallbackOption]);

  // Memoize filtered courses for performance
  const filteredCourses = useMemo(() => {
    // Deduplicate courses by display code to avoid duplicate keys
    const uniqueCourses = courses.reduce((acc, course) => {
      const { code } = getCourseDisplay(course);
      if (code && !acc.some((c) => getCourseDisplay(c).code === code)) {
        acc.push(course);
      }
      return acc;
    }, []);

    // When there is no search term, show the full list (Firestorm count is small: ~218)
    if (!inputValue || inputValue.length < 2) {
      return uniqueCourses;
    }

    const searchTerm = inputValue.toLowerCase();
    const filtered = uniqueCourses.filter((course) => {
      const { code, title } = getCourseDisplay(course);

      return (
        code.toLowerCase().includes(searchTerm) ||
        title.toLowerCase().includes(searchTerm)
      );
    });

    // Sort results to prioritize better matches
    const sorted = filtered.sort((a, b) => {
      const aDisplay = getCourseDisplay(a);
      const bDisplay = getCourseDisplay(b);
      const aCode = aDisplay.code.toLowerCase();
      const bCode = bDisplay.code.toLowerCase();
      const aTitle = aDisplay.title.toLowerCase();
      const bTitle = bDisplay.title.toLowerCase();

      // Priority 1: Exact matches first
      const aExactMatch =
        aCode === searchTerm || (!!aTitle && aTitle === searchTerm);
      const bExactMatch =
        bCode === searchTerm || (!!bTitle && bTitle === searchTerm);

      if (aExactMatch && !bExactMatch) return -1;
      if (bExactMatch && !aExactMatch) return 1;

      // Priority 2: Codes/titles that start with search term
      const aStartsWith =
        aCode.startsWith(searchTerm) ||
        (!!aTitle && aTitle.startsWith(searchTerm));
      const bStartsWith =
        bCode.startsWith(searchTerm) ||
        (!!bTitle && bTitle.startsWith(searchTerm));

      if (aStartsWith && !bStartsWith) return -1;
      if (bStartsWith && !aStartsWith) return 1;

      // Priority 3: Then alphabetical order by code
      return aCode.localeCompare(bCode);
    });

    // Firestore list is small (~218), so we can safely return all matches
    return sorted;
  }, [courses, inputValue]);

  const handleChange = (event, newValue) => {
    const { code, title } = getCourseDisplay(newValue);
    onChange(code || "");

    // Update input value to show full display text when selection is made
    if (newValue) {
      const fullText = title ? `${code} - ${title}` : code;
      setInputValue(fullText);
    } else {
      setInputValue("");
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === "Enter" && inputValue) {
      const searchTerm = inputValue.toLowerCase();

      // Find exact match first (check code and title)
      const exactMatch = filteredCourses.find((course) => {
        const { code, title } = getCourseDisplay(course);
        return (
          code.toLowerCase() === searchTerm ||
          (!!title && title.toLowerCase() === searchTerm)
        );
      });

      if (exactMatch) {
        event.preventDefault();
        const { code, title } = getCourseDisplay(exactMatch);
        onChange(code);
        const fullText = title ? `${code} - ${title}` : code;
        setInputValue(fullText);
        return;
      }

      // If no exact match, select the first option if available
      if (filteredCourses.length > 0) {
        event.preventDefault();
        const { code, title } = getCourseDisplay(filteredCourses[0]);
        onChange(code);
        const fullText = title ? `${code} - ${title}` : code;
        setInputValue(fullText);
      }
    }
  };

  const handleInputChange = (event, newInputValue, reason) => {
    // Allow typing to filter options, but do not change the selected value here.
    // We'll reset to the selected option on blur to prevent non-existing course selection.
    setInputValue(newInputValue);
  };

  const handleBlur = (event) => {
    const selected = selectedOption || fallbackOption;
    if (selected) {
      const { code, title } = getCourseDisplay(selected);
      const fullText = title ? `${code} - ${title}` : code;
      setInputValue(fullText);
    } else {
      setInputValue("");
    }
    if (props.onBlur) props.onBlur(event);
  };

  return (
    <Autocomplete
      value={selectedOption || fallbackOption}
      disablePortal={disablePortal}
      size={size}
      fullWidth={fullWidth}
      options={filteredCourses}
      getOptionLabel={(option) => {
        const { code, title } = getCourseDisplay(option);
        return title ? `${code} - ${title}` : code;
      }}
      isOptionEqualToValue={(option, value) => {
        const optionCode = getCourseDisplay(option).code;
        const valueCode = getCourseDisplay(value).code;
        return optionCode === valueCode;
      }}
      loading={showLoading}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      onBlur={handleBlur}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          variant="outlined"
          size={size}
          InputLabelProps={{
            shrink: true,
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 1,
            },
            ...sx,
          }}
          inputProps={{ ...params.inputProps }}
          {...props}
        />
      )}
      renderOption={(props, option) => {
        const { code, title } = getCourseDisplay(option);
        const displayText = title ? `${code} - ${title}` : code;

        return (
          <li {...props} key={code}>
            <Box
              sx={{ display: "flex", flexDirection: "column", width: "100%" }}
            >
              <Typography variant="body2" noWrap>
                {displayText}
              </Typography>
            </Box>
          </li>
        );
      }}
    />
  );
};

export default SearchableCourseSelect;
